import React, { Suspense, useMemo } from "react";

import type { ConfigurableTableProps } from "../../types/tables";

// Lazy load the actual ConfigurableTable component
const ConfigurableTable = React.lazy(() =>
  import("./ConfigurableTable").then((module) => ({
    default: module.ConfigurableTable,
  })),
);

// Loading component for lazy loading
function TableLoadingSkeleton() {
  return (
    <div className="w-full animate-pulse">
      <div className="mb-4">
        <div className="h-6 bg-bg-secondary rounded w-48 mb-4"></div>
      </div>

      {/* Table header */}
      <div className="bg-bg-secondary h-11 rounded-t-xl mb-1 flex items-center px-6">
        <div className="grid grid-cols-6 gap-4 w-full">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="h-4 bg-bg-tertiary rounded" key={i}></div>
          ))}
        </div>
      </div>

      {/* Table rows */}
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <div
          className="bg-bg-primary h-11 border-b border-border-secondary flex items-center px-6"
          key={rowIndex}
        >
          <div className="grid grid-cols-6 gap-4 w-full">
            {Array.from({ length: 6 }).map((_, colIndex) => (
              <div
                className="h-4 bg-bg-tertiary rounded opacity-60"
                key={colIndex}
              ></div>
            ))}
          </div>
        </div>
      ))}

      {/* Pagination skeleton */}
      <div className="bg-bg-primary rounded-b-xl p-4 flex justify-between items-center border-t border-border-secondary">
        <div className="h-8 bg-bg-secondary rounded w-32"></div>
        <div className="flex gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div className="h-8 w-8 bg-bg-secondary rounded" key={i}></div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface LazyConfigurableTableProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> extends ConfigurableTableProps<T> {
  count?: number; // Optional count
  isCountLoading?: boolean; // Whether count data is still loading
  isVisible?: boolean; // Control whether the table should be rendered
}

/**
 * Lazy-loaded ConfigurableTable component with intelligent loading
 * Only renders when visible and provides smooth loading experience
 *
 * Smart Loading Optimization:
 * - If count is known to be 0, skip loading animation and show empty state immediately
 * - If count is unknown or > 0, show loading animation as normal
 */
export function LazyConfigurableTable<
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  count,
  isCountLoading = false,
  isLoading,
  isVisible = true,
  ...props
}: LazyConfigurableTableProps<T>) {
  // Smart loading logic: Skip loading animation if count is known to be 0
  const shouldShowLoading = useMemo(() => {
    // If we don't have count info yet, show loading as normal
    if (count === undefined || isCountLoading) {
      return isLoading;
    }

    // If count is 0, skip loading and show empty state immediately
    if (count === 0) {
      return false;
    }

    // Otherwise, show loading normally
    return isLoading;
  }, [count, isCountLoading, isLoading]);

  // Don't render anything if not visible (saves resources)
  if (!isVisible) {
    return null;
  }

  return (
    <Suspense fallback={<TableLoadingSkeleton />}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <ConfigurableTable {...(props as any)} isLoading={shouldShowLoading} />
    </Suspense>
  );
}

// Named export for compatibility
export { LazyConfigurableTable as ConfigurableTable };
